import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fmtNum, fmtEur, kmlTone, diffTone } from "@/lib/format";

export const EditEntryDialog = ({ entry, isAdmin, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry) setForm({
      date: entry.date, vehicles_out: entry.vehicles_out ?? "", kilometers: entry.kilometers ?? "", total_amount: entry.total_cost,
      fuel_price_per_liter: entry.fuel_price_per_liter ?? "", status: entry.status, admin_notes: entry.admin_notes || "",
    });
  }, [entry]);

  if (!entry || !form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const lt = Number(form.fuel_price_per_liter) > 0 ? Number(form.total_amount) / Number(form.fuel_price_per_liter) : 0;
  const kml = lt > 0 ? Number(form.kilometers) / lt : 0;

  const save = async () => {
    setSaving(true);
    const n = (v) => (v === "" || v === null ? undefined : Number(v));
    const payload = { date: form.date, vehicles_out: n(form.vehicles_out), kilometers: n(form.kilometers), total_amount: n(form.total_amount), fuel_price_per_liter: n(form.fuel_price_per_liter) };
    if (isAdmin) Object.assign(payload, { status: form.status, admin_notes: form.admin_notes });
    try {
      await api.put(`/entries/${entry.id}`, payload);
      toast.success("Record aggiornato");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-entry-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isAdmin ? "Modifica record" : "Correggi record"}</DialogTitle>
          <DialogDescription>{entry.station_name}{!isAdmin && " — dopo il salvataggio il record tornerà in attesa di approvazione."}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={form.date} onChange={set("date")} data-testid="edit-date-input" /></div>
          <div className="space-y-1.5"><Label>Mezzi usciti</Label><Input type="number" step="1" min="1" value={form.vehicles_out} onChange={set("vehicles_out")} data-testid="edit-vehicles-out-input" /></div>
          <div className="space-y-1.5"><Label>KM percorsi</Label><Input type="number" step="0.1" value={form.kilometers} onChange={set("kilometers")} data-testid="edit-km-input" /></div>
          <div className="space-y-1.5"><Label>Importo totale €</Label><Input type="number" step="0.01" value={form.total_amount} onChange={set("total_amount")} data-testid="edit-amount-input" /></div>
          <div className="space-y-1.5"><Label>Prezzo €/L</Label><Input type="number" step="0.001" value={form.fuel_price_per_liter} onChange={set("fuel_price_per_liter")} data-testid="edit-price-input" /></div>
          <div className="space-y-1.5"><Label>Litri calcolati</Label>
            <div className="flex h-9 items-center rounded-md border bg-slate-50 px-3 font-mono-num text-sm font-semibold" data-testid="edit-liters-value">{fmtNum(lt, 2)} L</div>
          </div>
          <div className="col-span-2 space-y-1.5"><Label>Consumo</Label>
            <div className={`flex h-9 items-center rounded-md border px-3 font-mono-num text-sm font-semibold ${kmlTone(kml)}`} data-testid="edit-kml-value">{fmtNum(kml, 2)} km/l</div>
          </div>
          {isAdmin && (
            <>
              <div className="col-span-2 space-y-1.5"><Label>Stato</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">In attesa</SelectItem>
                    <SelectItem value="Approved">Approvato</SelectItem>
                    <SelectItem value="Rejected">Segnalato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1.5"><Label>Note amministrazione</Label><Textarea value={form.admin_notes} onChange={set("admin_notes")} data-testid="edit-notes-input" /></div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-cancel-button">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save-button">{saving ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ReimbursementDialog = ({ entry, onClose, onSaved }) => {
  const [form, setForm] = useState({ km: "", rate: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (entry) setForm({ km: entry.reimbursed_km ?? "", rate: entry.reimbursement_rate ?? "" });
  }, [entry]);
  if (!entry) return null;
  const amount = form.km !== "" && form.rate !== "" ? Number(form.km) * Number(form.rate) : null;
  const diff = amount === null ? null : amount - entry.total_cost_net;

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/entries/${entry.id}/reimbursement`, {
        reimbursed_km: form.km === "" ? null : Number(form.km),
        reimbursement_rate: form.rate === "" ? null : Number(form.rate),
      });
      toast.success("Rimborso salvato");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reimbursement-dialog">
        <DialogHeader>
          <DialogTitle>Rimborso Amazon</DialogTitle>
          <DialogDescription>{entry.station_name} — {entry.date}. Rimborso al netto IVA; la differenza è calcolata sul gasolio al netto dell'IVA 22%. Dato visibile solo all'amministrazione.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Km rimborsati</Label><Input type="number" step="0.1" min="0" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} data-testid="reimb-km-input" /></div>
          <div className="space-y-1.5"><Label>Tariffa rimborso (€/km)</Label><Input type="number" step="0.001" min="0" placeholder="es. 0,215" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="reimb-rate-input" /></div>
        </div>
        <div className="grid grid-cols-3 gap-4 rounded-lg bg-slate-50 p-4 text-sm">
          <div><p className="text-xs text-slate-500">Importo rimborso (netto)</p><p className="font-mono-num font-semibold" data-testid="reimb-amount-value">{amount === null ? "—" : fmtEur(amount)}</p></div>
          <div><p className="text-xs text-slate-500">Gasolio netto IVA</p><p className="font-mono-num font-semibold" data-testid="reimb-fuel-net-value">{fmtEur(entry.total_cost_net)}</p><p className="text-[11px] text-slate-400">{fmtEur(entry.total_cost)} IVA incl.</p></div>
          <div><p className="text-xs text-slate-500">Differenza netta</p><p className={`font-mono-num font-semibold ${diffTone(diff)}`} data-testid="reimb-diff-value">{diff === null ? "—" : fmtEur(diff)}</p></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reimb-cancel-button">Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="reimb-save-button">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RejectDialog = ({ entry, onClose, onSaved }) => {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => setNote(entry?.admin_notes || ""), [entry]);
  if (!entry) return null;

  const submit = async () => {
    if (note.trim().length < 3) return toast.error("Inserisci una nota di almeno 3 caratteri");
    setSaving(true);
    try {
      await api.post(`/entries/${entry.id}/reject`, { note });
      toast.success("Anomalia segnalata alla stazione");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reject-entry-dialog">
        <DialogHeader>
          <DialogTitle>Segnala errore</DialogTitle>
          <DialogDescription>{entry.station_name} — la nota sarà visibile alla stazione.</DialogDescription>
        </DialogHeader>
        <Textarea rows={4} placeholder="Descrivi l'anomalia riscontrata..." value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-note-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reject-cancel-button">Annulla</Button>
          <Button variant="destructive" onClick={submit} disabled={saving} data-testid="submit-reject-note-button">Segnala</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
